/**
 * Compliance Command — shared UI tokens.
 * Use these instead of one-off Tailwind strings in new/refactored components.
 */

/** Z-index scale */
export const zIndex = {
  hud: 10,
  hudPanel: 30,
  inspector: 40,
  modal: 50,
  modalHigh: 60,
  menu: 70,
} as const;

/** Surface backgrounds */
export const surfaces = {
  hud: 'bg-slate-900/90 border border-slate-600 rounded-xl backdrop-blur-md shadow-2xl',
  panel: 'bg-slate-950/95 border border-slate-700/80 rounded-xl backdrop-blur-md shadow-2xl',
  panelInset: 'bg-slate-900/50 border border-slate-800 rounded-lg',
  commandCenter: 'bg-[#050505] border border-cyan-900/50 rounded-lg shadow-2xl',
  overlay: 'bg-black/80 backdrop-blur-md',
  overlayLight: 'bg-black/70 backdrop-blur-sm',
} as const;

/** Typography */
export const type = {
  eyebrow: 'text-[10px] font-bold uppercase tracking-widest text-slate-500',
  eyebrowAccent: 'text-[10px] font-bold uppercase tracking-widest text-cyan-500/80',
  title: 'text-lg font-bold text-white tracking-wide',
  titleLg: 'text-xl font-black text-white tracking-wide',
  body: 'text-sm text-slate-300',
  caption: 'text-xs text-slate-400',
  mono: 'font-mono',
} as const;

/** Semantic info cards (Command Center pattern) */
export const infoCards = {
  enables: 'rounded-lg border border-emerald-800/40 bg-emerald-950/20 px-3 py-2.5',
  enablesLabel: 'text-[9px] font-bold uppercase tracking-widest text-emerald-400/90 mb-1',
  enablesText: 'text-xs text-emerald-100/90 leading-relaxed',
  value: 'rounded-lg border border-amber-800/40 bg-amber-950/20 px-3 py-2.5',
  valueLabel: 'text-[9px] font-bold uppercase tracking-widest text-amber-400/90 mb-1',
  valueText: 'text-xs text-amber-100/90 leading-relaxed',
  chip: 'text-[10px] text-slate-400 bg-slate-800/60 border border-slate-700/60 rounded-full px-2.5 py-1',
} as const;

/** Buttons */
export const buttons = {
  primary: 'py-3 bg-cyan-700 hover:bg-cyan-600 text-white font-bold rounded-lg uppercase text-xs tracking-widest transition-colors',
  primarySm: 'py-2 px-4 bg-cyan-600 hover:bg-cyan-500 text-white font-bold rounded text-[10px] uppercase tracking-widest transition-colors',
  resume: 'py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-lg uppercase text-xs tracking-widest transition-all shadow-lg',
  ghost: 'py-2 border border-slate-700 hover:bg-slate-800 text-slate-400 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors',
  danger: 'py-2 border border-red-900/30 text-red-500 hover:bg-red-900/10 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors',
  icon: 'text-slate-400 hover:text-white transition-colors p-1',
  tab: 'flex-1 py-3 text-[10px] font-bold uppercase tracking-widest transition-colors',
  tabActive: 'text-cyan-400 border-b-2 border-cyan-400 bg-slate-900/50',
  tabIdle: 'text-slate-500 hover:text-slate-300',
} as const;

/** Tab nav (sidebar) */
export const nav = {
  item: 'text-left px-3 py-2.5 rounded-lg text-xs transition-all flex flex-col gap-0.5 border',
  itemActive: 'bg-cyan-900/25 text-cyan-300 border-cyan-500/30 shadow-[0_0_12px_rgba(6,182,212,0.12)]',
  itemIdle: 'text-slate-500 hover:text-slate-300 hover:bg-white/5 border-transparent',
  itemSub: 'text-[9px] leading-snug pl-6',
  itemSubActive: 'text-cyan-200/70',
  itemSubIdle: 'text-slate-600 group-hover:text-slate-500',
} as const;

/** Theme accents for specialized panels */
export const themes = {
  default: { accent: 'cyan', border: 'border-slate-700', text: 'text-cyan-400' },
  soc: { accent: 'green', border: 'border-green-900', text: 'text-green-500' },
  compliance: { accent: 'cyan', border: 'border-cyan-900/50', text: 'text-cyan-400' },
  danger: { accent: 'red', border: 'border-red-900', text: 'text-red-500' },
  success: { accent: 'emerald', border: 'border-emerald-800', text: 'text-emerald-400' },
} as const;

export function cn(...classes: (string | false | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}